import { type FormEvent, useEffect, useRef, useState } from 'react';
import { bootstrap, invitePreview, join, pending, relogin } from '#shared/api/auth';
import { fill } from '@/copy';
import { welcomeCopy as c } from '@/copy/welcome';
import { api, isApiError } from '@/lib/api';
import { readText } from '@/lib/clipboard';
import { persistStorage } from '@/lib/idb';
import { clearPendingCode, extractCode, formatCode, isInviteCode, isReloginCode, readPendingCode } from '@/lib/invite';
import { flushOutbox } from '@/lib/outbox';
import { deviceLabel, platformInfo } from '@/lib/platform';
import { toast } from '@/lib/toast';
import { Button, Field, Wordmark } from '../kit';
import { signedIn, useConfig } from '../session';

/**
 * Welcome, inside the installed app when nobody is signed in on this phone.
 *
 * - join: paste or type the crew code, check it (invite preview), then either a name for a crew
 *   code or "you're joining as Nassos" for a personal invite, and join. A code that came with an
 *   invite link is checked on its own.
 * - comeback: the "Get them back in" entry screen. A crewmate makes a six character code for you
 *   (Crew, your name, Get them back in); you type it here within 15 minutes. The owner's rescue
 *   code (eight characters, from the server command) works in the same box.
 * - bootstrap: the one-time owner setup, only offered while the server has no owner.
 */
type Preview = Awaited<ReturnType<typeof previewCode>>;
type Mode = 'join' | 'relogin' | 'bootstrap';

function previewCode(code: string) {
  return api.call(invitePreview, { params: { code } });
}

/** What to tell the person. The server's own 4xx messages are already written for humans. */
export function errorText(e: unknown, online = navigator.onLine): string {
  if (!online) return c.offline;
  if (isApiError(e)) {
    if (e.status === 429) return c.tooMany;
    if (e.status === 404) return c.badCode;
    if (e.status < 500 && e.message && e.status !== 428 && e.status !== 401) return e.message;
    if (e.status === 400 || e.code === 'INVALID_CODE') return c.badCode;
  }
  return c.failed;
}

const BIG_CODE =
  '[&_input]:font-display [&_input]:font-black [&_input]:italic [&_input]:text-center [&_input]:text-3xl [&_input]:tracking-widest';

export function Welcome({ initialCode }: { initialCode: string | null }) {
  const config = useConfig();
  const [mode, setMode] = useState<Mode>('join');
  const prefilled = useRef(initialCode ?? readPendingCode());
  const [code, setCode] = useState(() => (prefilled.current ? formatCode(prefilled.current) : ''));
  const [preview, setPreview] = useState<Preview | null>(null);
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoChecked = useRef(false);

  useEffect(() => {
    if (code) return;
    // The Lax pending cookie from /j/:code is a bonus (Android shares it; iOS may not).
    api
      .call(pending, {})
      .then((r) => {
        if (r.code) setCode((prev) => prev || r.code || '');
      })
      .catch(() => {});
  }, [code]);

  const device = {
    platform: platformInfo().platform,
    deviceName: deviceLabel(navigator.userAgent, navigator.maxTouchPoints),
  };

  const done = async () => {
    clearPendingCode();
    void persistStorage();
    if (window.location.pathname.startsWith('/j/')) window.history.replaceState(null, '', '/');
    toast({ title: c.joined, tone: 'success' });
    await signedIn();
    // Plays this phone kept while it was signed out go up as soon as it is back in.
    void flushOutbox();
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const check = (norm: string) => run(async () => setPreview(await previewCode(norm)));

  // A code that arrived with the invite link is checked right away, so a personal invite shows
  // "you're joining as ..." without a tap. Typed and remembered codes wait for the button.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on arrival
  useEffect(() => {
    if (autoChecked.current || !initialCode) return;
    autoChecked.current = true;
    const norm = extractCode(initialCode);
    if (norm && isInviteCode(norm)) void check(norm);
  }, [initialCode]);

  const pickMode = (next: Mode) => {
    // The crew code that arrived with the link is not a comeback code: do not leave it in that box.
    if (next === 'relogin' && prefilled.current && extractCode(code) === extractCode(prefilled.current)) setCode('');
    setMode(next);
    setError(null);
    setPreview(null);
  };

  const onPaste = async () => {
    const text = await readText();
    if (text === null) {
      setError(c.pasteBlocked);
      return;
    }
    const found = extractCode(text);
    if (!found) {
      setError(c.pasteEmpty);
      return;
    }
    setError(null);
    setCode(formatCode(found));
    if (isReloginCode(found)) setMode('relogin');
    // A pasted crew code goes straight to the name step, like a code that came with the link.
    else if (mode === 'join' && isInviteCode(found)) void check(found);
  };

  const onCheck = (e: FormEvent) => {
    e.preventDefault();
    const norm = extractCode(code);
    if (norm && isReloginCode(norm)) {
      // Somebody typed their comeback code into the crew box. Take them to the right screen.
      pickMode('relogin');
      setCode(norm);
      return;
    }
    if (!norm || !isInviteCode(norm)) {
      setError(c.badCode);
      return;
    }
    void check(norm);
  };

  const onJoin = (e: FormEvent) => {
    e.preventDefault();
    const norm = extractCode(code);
    if (!norm) return;
    const personal = preview?.kind === 'personal' && preview.player;
    void run(async () => {
      await api.call(join, {
        body: {
          code: norm,
          ...device,
          ...(personal
            ? {}
            : { profile: { displayName: name.trim(), ...(nickname.trim() ? { nickname: nickname.trim() } : {}) } }),
        },
      });
      await done();
    });
  };

  const onRelogin = (e: FormEvent) => {
    e.preventDefault();
    const norm = extractCode(code);
    if (!norm) {
      setError(c.relogin.short);
      return;
    }
    void run(async () => {
      await api.call(relogin, { body: { code: norm, ...device } });
      await done();
    });
  };

  const onBootstrap = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.call(bootstrap, { body: { token: token.trim(), displayName: name.trim(), ...device } });
      await done();
    });
  };

  const comeback = mode === 'relogin';

  return (
    <main className="min-h-dvh bg-lilac text-ink">
      <div className="tts-pt-safe tts-pb-safe">
        <div className="mx-auto flex max-w-md flex-col gap-6 px-5 pt-10 pb-10">
          <div className="flex justify-center">
            <Wordmark variant="sticker" width={170} />
          </div>
          <header className="flex flex-col items-start gap-3">
            <h1 className="tts-plate tts-display bg-butter text-[1.8rem]">
              <span>{comeback ? c.relogin.title : c.title}</span>
            </h1>
            <p className="text-[1.05rem] font-semibold">{comeback ? c.relogin.body : c.body}</p>
          </header>

          {mode === 'join' && !preview && (
            <form onSubmit={onCheck} className="tts-card flex flex-col gap-4 p-4">
              <Button variant="accent" size="lg" icon="copy" onClick={onPaste} block>
                {c.paste}
              </Button>
              <Field
                label={c.codeLabel}
                hint={c.codeHint}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={c.codePlaceholder}
                autoCapitalize="characters"
                autoComplete="one-time-code"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
                maxLength={12}
                className={BIG_CODE}
              />
              <Button type="submit" variant="primary" size="lg" disabled={busy || code.trim().length < 6} block>
                {busy ? c.checking : c.check}
              </Button>
            </form>
          )}

          {mode === 'join' && preview && (
            <form onSubmit={onJoin} className="tts-card flex flex-col gap-4 p-4">
              <p className="text-lg font-bold">
                {preview.invitedBy
                  ? fill(c.invitedBy, { name: preview.invitedBy, crew: preview.crewName })
                  : fill(c.invitedCrew, { crew: preview.crewName })}
              </p>
              {preview.kind === 'personal' && preview.player ? (
                <p className="tts-card-pale px-3 py-2 font-semibold" data-testid="welcome-personal">
                  {fill(c.joiningAs, { name: preview.player.displayName })}
                </p>
              ) : (
                <>
                  <Field
                    label={c.nameLabel}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={c.namePlaceholder}
                    autoComplete="given-name"
                    maxLength={40}
                    required
                  />
                  <Field
                    label={c.nicknameLabel}
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    maxLength={24}
                  />
                </>
              )}
              <Button
                type="submit"
                variant="go"
                size="xl"
                disabled={busy || (preview.kind !== 'personal' && !name.trim())}
                block
              >
                {busy ? c.joining : c.join}
              </Button>
              <Button variant="ghost" onClick={() => setPreview(null)}>
                {c.back}
              </Button>
            </form>
          )}

          {comeback && (
            <>
              <ol className="tts-card-pale flex flex-col gap-2 px-4 py-3" data-testid="welcome-comeback-steps">
                {c.relogin.steps.map((s, i) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="tts-display grid size-7 shrink-0 place-items-center rounded-chip border-3 border-ink bg-lavender text-sm">
                      {i + 1}
                    </span>
                    <span className="pt-0.5 font-medium leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
              <form onSubmit={onRelogin} className="tts-card flex flex-col gap-4 p-4">
                <Button variant="accent" size="lg" icon="copy" onClick={onPaste} block>
                  {c.paste}
                </Button>
                <Field
                  label={c.relogin.label}
                  hint={c.relogin.help}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  autoCapitalize="characters"
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={12}
                  className={BIG_CODE}
                />
                <Button type="submit" variant="go" size="lg" disabled={busy || code.trim().length < 6} block>
                  {busy ? c.relogin.acting : c.relogin.action}
                </Button>
                <Button variant="ghost" onClick={() => pickMode('join')}>
                  {c.relogin.joinInstead}
                </Button>
              </form>
              <p className="text-sm font-medium">{c.relogin.owner}</p>
            </>
          )}

          {mode === 'bootstrap' && (
            <form onSubmit={onBootstrap} className="tts-card flex flex-col gap-4 p-4">
              <Field
                label={c.bootstrap.label}
                hint={c.bootstrap.help}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
                type="password"
              />
              <Field label={c.nameLabel} value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
              <Button type="submit" variant="go" size="lg" disabled={busy || !token || !name.trim()} block>
                {c.bootstrap.action}
              </Button>
              <Button variant="ghost" onClick={() => pickMode('join')}>
                {c.back}
              </Button>
            </form>
          )}

          {error && (
            <p role="alert" className="tts-card bg-peach px-4 py-3 font-bold">
              {error}
            </p>
          )}

          {mode === 'join' && (
            <div className="flex flex-col items-start gap-1 text-sm font-bold">
              <button
                type="button"
                className="min-h-11 underline underline-offset-4"
                onClick={() => pickMode('relogin')}
              >
                {c.relogin.toggle}
              </button>
              {config.data?.bootstrapNeeded && (
                <button
                  type="button"
                  className="min-h-11 underline underline-offset-4"
                  onClick={() => pickMode('bootstrap')}
                >
                  {c.bootstrap.toggle}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
